import * as Mui from '@mui/material';
import React from 'react';
import { LW_POS_WHITE } from '../../constants';
import { useStartupLoader } from '../../hooks';
import { CommonBtn } from '../../components';
import { keyframes } from '@mui/system';

// Subtle floating animation for the logo
const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-15px); }
  100% { transform: translateY(0px); }
`;

// Shimmer effect for the text
const shimmer = keyframes`
  0% { opacity: 0.6; }
  50% { opacity: 1; }
  100% { opacity: 0.6; }
`;

const StartupLoader: React.FC = () => {
  const theme = Mui.useTheme();
  const { message } = useStartupLoader();

  return (
    <Mui.Stack
      height={"100vh"}
      justifyContent={"center"}
      alignItems={"center"}
      sx={{
        position: 'relative',
        overflow: 'hidden',
        background: `radial-gradient(circle at 50% 50%, ${theme.palette.secondary.dark} 0%, ${theme.palette.common.black} 100%)`,
        // Abstract subtle glow in the background
        '&::before': {
          content: '""',
          position: 'absolute',
          width: '150%',
          height: '150%',
          background: `radial-gradient(circle, ${theme.palette.primary.main}15 0%, transparent 50%)`,
          animation: `${shimmer} 8s infinite ease-in-out`,
        }
      }}
    >
      {/* Content Container with Glass Effect */}
      <Mui.Stack
        spacing={4}
        alignItems="center"
        sx={{ zIndex: 2, textAlign: 'center', px: 3 }}
      >
        {/* Brand Name with Letter Spacing */}
        <Mui.Typography
          variant="h2"
          sx={{
            fontWeight: 800,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            background: `linear-gradient(45deg, ${theme.palette.primary.light} 30%, ${theme.palette.primary.main} 90%)`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            filter: 'drop-shadow(0px 4px 10px rgba(0,0,0,0.3))',
            mb: -2
          }}
        >
          Express Fast
        </Mui.Typography>

        {/* Floating Logo Image */}
        <Mui.Box
          component="img"
          src={LW_POS_WHITE}
          alt="Product Logo"
          sx={{
            maxWidth: { xs: 300, md: 450 },
            height: 'auto',
            borderRadius: '40px',
            animation: `${float} 6s infinite ease-in-out`,
            filter: 'drop-shadow(0px 20px 30px rgba(0,0,0,0.5))',
          }}
        />

        {/* Loader Section */}
        <Mui.Box sx={{ width: 300, mt: 2 }}>
          <Mui.LinearProgress
            sx={{
              height: 6,
              borderRadius: 3,
              backgroundColor: 'rgba(255,255,255,0.1)',
              '& .MuiLinearProgress-bar': {
                borderRadius: 3,
                background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
              },
            }}
          />
          
          <Mui.Typography
            variant="body1"
            sx={{
              mt: 2,
              color: 'rgba(255,255,255,0.7)',
              fontWeight: 500,
              letterSpacing: 1,
              animation: `${shimmer} 2s infinite`,
            }}
          >
            {message || "Initializing System..."}
          </Mui.Typography>
        </Mui.Box>

        {/* Troubleshooting Section (Subtle) */}
        <Mui.Stack 
            spacing={2} 
            sx={{ 
                mt: 4, 
                p: 3, 
                borderRadius: 4, 
                backdropFilter: 'blur(10px)',
                backgroundColor: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.05)'
            }}
        >
          <Mui.Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', maxWidth: 250 }}>
            Taking longer than expected? Clear cache and try again.
          </Mui.Typography>
          
          <CommonBtn
            variant="outlined"
            onClick={() => {
              localStorage.clear();
              window.location.reload();
            }}
            sx={{
              borderColor: 'rgba(255,255,255,0.2)',
              color: '#fff',
              '&:hover': {
                borderColor: theme.palette.primary.main,
                backgroundColor: 'rgba(255,255,255,0.05)',
              },
              textTransform: 'none',
              borderRadius: '12px',
              px: 4
            }}
          >
            Force Reload
          </CommonBtn>
        </Mui.Stack>
      </Mui.Stack>
    </Mui.Stack>
  );
};

export { StartupLoader };